import type { NwsAlert, AlertSeverity } from './types'
import type { ThreatLevel } from '@/lib/criteria/levels'
import { highestLevel } from '@/lib/criteria/levels'

/**
 * Map a single NWS alert → a ThreatLevel. Severity is the primary axis, but a few
 * event keywords escalate (an emergency or tornado warning outranks its raw severity).
 * This is intentionally the *only* place alert→level lives, so the Milestone-2
 * criteria engine can absorb it wholesale.
 */
export function alertLevel(alert: NwsAlert): ThreatLevel {
  const event = alert.event.toLowerCase()

  if (/emergency|tornado warning|flash flood emergency|particularly dangerous/.test(event)) {
    return 'extreme'
  }
  if (/warning/.test(event)) {
    return alert.severity === 'Extreme' ? 'extreme' : 'severe'
  }
  if (/watch/.test(event)) return 'watch'
  if (/advisory|statement|outlook/.test(event)) return 'info'

  return severityToLevel(alert.severity)
}

function severityToLevel(s: AlertSeverity): ThreatLevel {
  switch (s) {
    case 'Extreme':
      return 'extreme'
    case 'Severe':
      return 'severe'
    case 'Moderate':
      return 'warning'
    case 'Minor':
      return 'watch'
    default:
      return 'info'
  }
}

/** Roll a set of active alerts up into one location threat level. */
export function deriveLevelFromAlerts(alerts: NwsAlert[]): ThreatLevel {
  if (alerts.length === 0) return 'quiet'
  return highestLevel(alerts.map(alertLevel))
}
